import { useEffect, useState } from "react";
import axios from "v2/utils/axios";
import { SlaConfigurationData } from "../types";
import { LocalNotification } from "lib";

const DEFAULT_CONFIG: Omit<SlaConfigurationData, "id" | "company_id" | "updated_at"> = {
  upcoming_window_days: 7,
  email_assignee_on_upcoming: false,
  reminder_on_at_risk: true,
  escalate_manager_on_at_risk: true,
  escalate_portfolio_on_breached: true,
  escalate_executive_after_24h: true,
  send_daily_digest: false,
};

const DEFAULT_LOAD_ERROR = "Failed to load SLA configuration. Please refresh and try again.";

interface UseSlaConfigurationResult {
  config: SlaConfigurationData | null;
  isLoading: boolean;
  loadError: boolean;
  loadErrorMessage: string | null;
  isSaving: boolean;
  unauthorized: boolean;
  save: (data: Partial<SlaConfigurationData>) => Promise<void>;
}

export function useSlaConfiguration(): UseSlaConfigurationResult {
  const [config, setConfig] = useState<SlaConfigurationData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setLoadError(false);
    setLoadErrorMessage(null);
    setUnauthorized(false);
    axios
      .get(`${process.env.REACT_APP_API_ENDPOINT_V2}sla_configuration`)
      .then((res) => {
        const attrs = res.data?.data?.attributes ?? res.data;
        const id = res.data?.data?.id ?? "";
        setConfig({ id, ...attrs } as SlaConfigurationData);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          setUnauthorized(true);
        } else {
          console.error("Error fetching SLA configuration:", err);
          // Mark the load as errored so the form can disable Save.
          // We still populate defaults so the checkboxes are visible — but
          // the caller must check loadError before allowing a save.
          // Prefer the API-provided message (e.g. "your account does not
          // belong to a company") so the user sees an actionable reason.
          setLoadError(true);
          setLoadErrorMessage(err?.response?.data?.errors?.detail || DEFAULT_LOAD_ERROR);
          setConfig({ id: "", company_id: "", updated_at: "", ...DEFAULT_CONFIG } as SlaConfigurationData);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const save = async (data: Partial<SlaConfigurationData>) => {
    setIsSaving(true);
    try {
      const res = await axios.put(`${process.env.REACT_APP_API_ENDPOINT_V2}sla_configuration`, {
        sla_configuration: data,
      });
      const attrs = res.data?.data?.attributes ?? res.data;
      const id = res.data?.data?.id ?? config?.id ?? "";
      setConfig({ id, ...attrs } as SlaConfigurationData);
      setLoadError(false);
      new LocalNotification("Configuration saved");
    } catch (err) {
      console.error("Error saving SLA configuration:", err);
      new LocalNotification("Something went wrong", "Error", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return { config, isLoading, loadError, loadErrorMessage, isSaving, unauthorized, save };
}
